import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { User, Search, Check, Upload, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { PageHero, Empty } from "../components/ui-parts";
import { ODISHA_DISTRICTS } from "../lib/districts";

const INIT = { full_name: "", photo_url: "", mobile: "", email: "", district: "", block: "", address: "", qualification: "", date_of_birth: "", occupation: "", gender: "", sanskrit_level: "", intro: "", show_mobile: false, show_email: false };

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_EXT = ["jpg", "jpeg", "png"];
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png"];

export default function Volunteers() {
  const [tab, setTab] = useState("directory");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [district, setDistrict] = useState("");
  const [form, setForm] = useState(INIT);
  const [done, setDone] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(""); // dataURL for preview
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/volunteers", { params: { district: district || undefined, q: q || undefined } }).then(r => setList(r.data)); }, [district, q]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const ct = (file.type || "").toLowerCase();
    if (!ALLOWED_PHOTO_EXT.includes(ext) || !ALLOWED_PHOTO_TYPES.includes(ct)) {
      toast.error("Please upload a JPG, JPEG or PNG image");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be 5 MB or smaller");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result || "");
    reader.readAsDataURL(file);
    // Upload to backend to get a stored file id
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/volunteer-photo", fd);
      setForm(f => ({ ...f, photo_url: data.id }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
      setPhotoPreview("");
      setForm(f => ({ ...f, photo_url: "" }));
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setPhotoUploading(false);
    }
  };

  const clearPhoto = () => {
    setPhotoPreview("");
    setForm(f => ({ ...f, photo_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo_url) { toast.error("Please upload a profile photo"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/volunteers/register", form);
      setDone(data.id);
      setForm(INIT);
      setPhotoPreview("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Thanks! Your registration is pending admin approval.");
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="volunteers-page">
      <PageHero badge="Volunteer" title="Volunteer Network" subtitle="Join a growing Sanskrit movement across Odisha. Teach, organise, translate, or simply help spread the joy of Sanskrit." />
      <section className="section">
        <div className="container-narrow">
          <div className="flex gap-2 mb-8">
            <button onClick={() => setTab("directory")} data-testid="tab-directory" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "directory" ? "bg-[var(--js-primary)] text-white" : "bg-white border border-[var(--js-border)]"}`}>Directory</button>
            <button onClick={() => setTab("register")} data-testid="tab-register" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "register" ? "bg-[var(--js-primary)] text-white" : "bg-white border border-[var(--js-border)]"}`}>Register</button>
          </div>

          {tab === "directory" && (
            <>
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--js-muted)]" />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="w-full pl-10 pr-3 py-2 rounded-full border border-[var(--js-border)] bg-white text-sm" data-testid="vol-search" />
                </div>
                <select value={district} onChange={e => setDistrict(e.target.value)} className="px-3 py-2 rounded-full border border-[var(--js-border)] bg-white text-sm" data-testid="vol-district-filter">
                  <option value="">All districts</option>{ODISHA_DISTRICTS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              {list.length === 0 ? <Empty title="No volunteers to show" hint="Registered volunteers will appear here after admin approval." /> :
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map(v => (
                    <div key={v.id} className="js-card p-5" data-testid={`vol-${v.id}`}>
                      <div className="flex items-center gap-3">
                        {v.photo_url ? <img src={fileUrl(v.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-[var(--js-bg)] border border-[var(--js-border)] flex items-center justify-center"><User size={16} /></div>}
                        <div><div className="font-semibold">{v.full_name}</div><div className="text-xs text-[var(--js-muted)]">{v.designation} · {v.district}</div></div>
                      </div>
                      {v.intro && <p className="mt-3 text-sm text-[var(--js-muted)] line-clamp-3">{v.intro}</p>}
                    </div>
                  ))}
                </div>
              }
            </>
          )}

          {tab === "register" && (
            <div className="js-card p-6 sm:p-8 max-w-3xl" data-testid="vol-register-form">
              {done ? <div className="text-center py-8"><Check size={40} className="mx-auto text-[var(--js-accent)]" /><p className="font-semibold mt-3">Registration received</p><p className="text-sm text-[var(--js-muted)] mt-1">Our team will review your details and photo and approve your profile soon.</p></div> :
                <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
                  {/* Mandatory photo upload */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Profile Photo <span className="text-[var(--js-primary)]">*</span></label>
                    <div className="flex items-start gap-4">
                      <div className="shrink-0">
                        {photoPreview ? (
                          <div className="relative" data-testid="vol-photo-preview-wrap">
                            <img src={photoPreview} alt="Profile preview" className="w-28 h-28 rounded-full object-cover border-2 border-[var(--js-primary)]" data-testid="vol-photo-preview" />
                            <button type="button" onClick={clearPhoto} aria-label="Remove photo" data-testid="vol-photo-clear"
                              className="absolute -top-1 -right-1 bg-white border border-[var(--js-border)] rounded-full p-1 shadow hover:bg-[var(--js-bg)]"><X size={12} /></button>
                          </div>
                        ) : (
                          <div className="w-28 h-28 rounded-full bg-[var(--js-bg)] border-2 border-dashed border-[var(--js-border)] flex items-center justify-center text-[var(--js-muted)]"><User size={32} /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <label className={`js-btn-outline !py-1.5 !px-3 !text-xs cursor-pointer ${photoUploading ? "opacity-70 pointer-events-none" : ""}`} data-testid="vol-photo-upload-btn">
                          <Upload size={12} /> {photoUploading ? "Uploading…" : photoPreview ? "Change photo" : "Upload photo"}
                          <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png" onChange={onPhotoPick} className="hidden" data-testid="vol-photo-input" required />
                        </label>
                        <p className="text-xs text-[var(--js-muted)] mt-2 leading-5">
                          Clear, well-lit photo of yourself.<br />
                          JPG, JPEG or PNG · Max 5 MB.
                        </p>
                      </div>
                    </div>
                  </div>

                  {[["full_name", "Full Name *"], ["mobile", "Mobile *"], ["email", "Email *"], ["block", "Block / City"], ["qualification", "Qualification"], ["occupation", "Occupation *"], ["sanskrit_level", "Sanskrit knowledge level"]].map(([k, l]) => (
                    <input key={k} required={l.includes("*")} type={k === "email" ? "email" : "text"} placeholder={l} value={form[k]} onChange={set(k)} data-testid={`vol-${k}`} className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" />
                  ))}
                  <div className="flex flex-col">
                    <label className="text-xs text-[var(--js-muted)] mb-1 ml-1">Date of Birth *</label>
                    <input required type="date" max={new Date().toISOString().split("T")[0]} value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="vol-date_of_birth" className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" />
                  </div>
                  <select required value={form.gender} onChange={set("gender")} data-testid="vol-gender" className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm">
                    <option value="">Gender *</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  <select required value={form.district} onChange={set("district")} data-testid="vol-district" className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm">
                    <option value="">District *</option>{ODISHA_DISTRICTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                  <textarea placeholder="Address" value={form.address} onChange={set("address")} data-testid="vol-address" className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm sm:col-span-2 h-16" />
                  <textarea placeholder="Short introduction" value={form.intro} onChange={set("intro")} data-testid="vol-intro" className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm sm:col-span-2 h-20" />
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_mobile} onChange={set("show_mobile")} data-testid="vol-show-mobile" /> Show my mobile publicly</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_email} onChange={set("show_email")} data-testid="vol-show-email" /> Show my email publicly</label>
                  <div className="sm:col-span-2">
                    <button disabled={submitting || photoUploading || !form.photo_url} className="js-btn-primary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="vol-submit">
                      {submitting ? "Submitting…" : "Register as Volunteer"}
                    </button>
                    {!form.photo_url && <p className="text-xs text-[var(--js-muted)] mt-2">Please upload a profile photo to continue.</p>}
                  </div>
                </form>
              }
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
